import { CSSProperties, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  Link,
  makeStyles,
  TableCell,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Skeleton } from "@material-ui/lab";
import { FiltersHelper } from "./FiltersHelper";
import apiRequest from "utils/apiRequestWithErrorCode";
import { downloadFromLink } from "utils/downloadFile";

import { EmailFlowLogReport } from "@dashboard-v3/api";
import { useSnackbar } from "notistack";

interface ItemProps {
  report: EmailFlowLogReport;
  loading: boolean;
  onCancelReport: (id: string) => void;
}

type ReportStatus = EmailFlowLogReport["status"];
type ItemStyleProps = { status: ReportStatus; isDownloading: boolean };

export default function ReportItem({
  report,
  loading,
  onCancelReport,
}: ItemProps) {
  const { t } = useTranslation("emailFlowLogs");
  const { enqueueSnackbar } = useSnackbar();
  const { id, createdAt, user, status, filters } = report;
  const [isDownloading, setIsDownloading] = useState(false);
  const classes = useStyles({ status, isDownloading });

  async function onDownloadReport() {
    setIsDownloading(true);
    try {
      const { downloadUrl } = await apiRequest<{ downloadUrl: string }>(
        "GET",
        `/emailflow-logs/reports/${id}/download-link`
      );

      if (!downloadUrl) throw new Error("No download URL received");

      downloadFromLink(downloadUrl);
    } catch (e) {
      console.error("Download failed:", e);

      enqueueSnackbar(t("reports.downloadError"), { variant: "error" });
    } finally {
      setIsDownloading(false);
    }
  }

  if (loading)
    return (
      <TableRow>
        <TableCell align="left">
          <Skeleton variant="text" />
        </TableCell>
        <TableCell align="left">
          <Skeleton variant="text" />
        </TableCell>
        <TableCell align="left">
          <Skeleton variant="text" />
        </TableCell>
        <TableCell align="left">
          <Skeleton variant="text" />
        </TableCell>
        <TableCell align="left">
          <Skeleton variant="text" />
        </TableCell>
      </TableRow>
    );

  return (
    <TableRow>
      <TableCell align="left">{new Date(createdAt).toLocaleString()}</TableCell>
      <TableCell className={classes.statusCell} align="left">
        <div className={classes.statusContainer}>
          {status === "PENDING" && (
            <CircularProgress
              size={16}
              style={{ color: statusColors.get("PENDING") }}
            />
          )}
          <Typography className={classes.statusText}>
            {t(`reports.status.${status}`)}
          </Typography>
        </div>
      </TableCell>
      <TableCell align="left">{user}</TableCell>
      <TableCell align="left">
        <FiltersHelper filters={filters} />
      </TableCell>
      <TableCell className={classes.actionsCell} align="center">
        {status === "DONE" && (
          <div className={classes.linkContainer}>
            <Link onClick={onDownloadReport}>{t("reports.downloadLink")}</Link>
            {isDownloading && (
              <CircularProgress
                size={16}
                style={{ color: statusColors.get("PENDING") }}
              />
            )}
          </div>
        )}

        {status === "PENDING" && (
          <div className={classes.linkContainer}>
            <Link role="button" onClick={() => onCancelReport(id)}>
              {t("common:cancel")}
            </Link>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

const statusColors = new Map<ReportStatus, CSSProperties["color"]>([
  ["DONE", green[500]],
  ["PENDING", "#009ed2"],
  ["FAILED", "#f44336"],
  ["CANCELED", "rgba(0, 0, 0, 0.54)"],
]);

const useStyles = makeStyles<Theme, ItemStyleProps>({
  statusCell: { width: "18%" },
  statusContainer: { display: "flex", gap: 10, alignItems: "center" },
  actionsCell: { width: "14.5%" },
  statusText: {
    fontSize: "0.875rem",
    color(props) {
      return statusColors.get(props.status);
    },
  },
  linkContainer: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    "& > a": {
      pointerEvents(props) {
        return props.isDownloading ? "none" : "auto";
      },
      color(props) {
        if (props.isDownloading) {
          return "rgba(0, 0, 0, 0.40)";
        }

        if (props.status === "PENDING") {
          return statusColors.get("FAILED");
        }
        return "#009ed2";
      },
      cursor(props) {
        return props.isDownloading ? "progress" : "pointer";
      },
      "&:hover": {
        textDecoration(props) {
          return props.isDownloading ? "none" : "underline";
        },
      },
    },
  },
});
